import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  AuditFieldChange,
  AuditLogItem,
} from "_interfaces/audit-log.interfaces";
import { historyDateTimeFormatter } from "_helper/timeFormatter";
import type {
  FormattedFieldChange,
  HistoryDataSidebarItem,
} from "components/shared/history-data-sidebar";
import { useAppTranslation } from "locale/useAppTranslation";
import { useAuditLogFeed } from "hooks/useAuditLogFeed";
import { displayUserName } from "_helper/actor";
import { deepEqual, diffArrayByKey, diffRecord } from "_helper/diff";
import { passthrough } from "_helper/valueFormat";
import { safeParse } from "_helper/json";
import { containsIgnoredKey, isIgnoredKey, titleCase, toSnakeCase } from "_helper/text";

/**
 * Driver's history hook — driver owns its data shapes and rules.
 *
 * Driver rows share the master-data audit contract (field_changes[] /
 * field_changed / snapshot pairs). Driver is the domain that carries
 * reasons (deactivations, deletions), so `reason` is populated here
 * and rendered by the timeline item's callout.
 *
 * Labels and sentences resolve through the historyDataSidebar
 * namespace (driver field keys in history-data-sidebar.json, en+id),
 * with title-case fallback for dynamic backend keys.
 *
 * Mechanics come from _helper; every driver-specific decision lives
 * here. buildDriverHistoryEntry is exported for the detail page's
 * print-only audit appendix, which fetches eagerly (print must work
 * without opening the sidebar).
 */

const IGNORED_KEYS = [
  "id",
  "created_at",
  "updated_at",
  "created_by",
  "updated_by",
  "deleted_at",
  "deleted_by",
  "driver_picture_url",
  "timestamp",
  "__v",
  "metadata",
];

// --- action vocabulary (driver service contract) ---

const normalizeActionType = (action?: string): string => {
  if (!action) return "UPDATE";
  const upper = action.toUpperCase();
  if (upper.includes("CREATE") || upper.includes("IMPORT")) return "CREATE";
  if (upper.includes("DELETE") || upper.includes("REMOVE")) return "DELETE";
  if (
    upper.includes("STATUS") ||
    upper.includes("ACTIVAT") ||
    upper.includes("DEACTIVAT") ||
    upper.includes("PUBLISH")
  ) {
    return "STATUS_UPDATED";
  }
  return "UPDATE";
};

const normalizeActionLabel = (action?: string): string => {
  if (!action) return "Edit";
  const upper = action.toUpperCase();
  if (upper === "CREATE" || upper === "CREATED") return "Create";
  if (upper === "IMPORT" || upper === "IMPORTED") return "Import";
  if (upper === "DELETE" || upper === "DELETED") return "Delete";
  if (upper.includes("ACTIVAT") || upper.includes("DEACTIVAT") || upper.includes("STATUS")) {
    return "Status Updated";
  }
  if (upper.includes("PUBLISH")) return "Publish";
  if (upper === "UPDATE" || upper === "UPDATED") return "Edit";
  return action;
};

const extractReason = (item: AuditLogItem): string | undefined => {
  const meta = item.metadata;
  if (!meta) return undefined;
  const value =
    meta.reason || meta.Reason || meta.reasons || meta.Reasons ||
    meta.deactivate_reason || meta.delete_reason || meta.update_reason ||
    meta.status_reason;
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
};

// --- field diffing over the dumb primitives ---

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const arrayKeyOf = (item: Record<string, unknown>): string =>
  String(item?.id || item?.name || JSON.stringify(item));

type Translate = (key: never, options?: Record<string, unknown>) => string;

const diffArrayField = (
  beforeValue: unknown,
  afterValue: unknown,
  title: string,
  fieldLabel: (key: string) => string,
  t: Translate,
  isCreate: boolean,
): FormattedFieldChange[] => {
  const out: FormattedFieldChange[] = [];
  const result = diffArrayByKey(
    (beforeValue ?? []) as Record<string, unknown>[],
    (afterValue ?? []) as Record<string, unknown>[],
    arrayKeyOf,
  );

  for (const item of result.added) {
    const name = arrayNameOf(item);
    out.push({
      fieldLabel: `${title} ${t("field-added" as never)}`,
      newValue: name,
      type: "add",
      sentence: t("add-text" as never, { field: title, value: name }),
    });
  }
  for (const item of result.removed) {
    const name = arrayNameOf(item);
    out.push({
      fieldLabel: `${title} ${t("field-deleted" as never)}`,
      oldValue: name,
      type: "delete",
      sentence: t("delete-text" as never, { field: title, value: name }),
    });
  }
  for (const pair of result.changed) {
    out.push(
      ...diffFields(pair.before, pair.after, arrayNameOf(pair.after), fieldLabel, t, isCreate),
    );
  }
  return out;
};

const arrayNameOf = (item: Record<string, unknown>): string =>
  String(item?.name || item?.full_address || "Item");

const diffFields = (
  before: unknown,
  after: unknown,
  parentLabel: string | undefined,
  fieldLabel: (key: string) => string,
  t: Translate,
  isCreate: boolean,
): FormattedFieldChange[] => {
  const out: FormattedFieldChange[] = [];
  const diffs = diffRecord(
    isRecord(before) ? before : undefined,
    isRecord(after) ? after : undefined,
    { skip: (key) => isIgnoredKey(key, IGNORED_KEYS) },
  );

  for (const diff of diffs) {
    const label = parentLabel
      ? `${parentLabel} - ${fieldLabel(diff.key)}`
      : fieldLabel(diff.key);

    if (Array.isArray(diff.before) || Array.isArray(diff.after)) {
      out.push(...diffArrayField(diff.before, diff.after, label, fieldLabel, t, isCreate));
      continue;
    }
    if (isRecord(diff.before) && isRecord(diff.after)) {
      out.push(...diffFields(diff.before, diff.after, label, fieldLabel, t, isCreate));
      continue;
    }

    const beforeStr = passthrough(diff.before);
    const afterStr = passthrough(diff.after);
    const sentence = isCreate
      ? t("create-text" as never, { field: label, new: afterStr })
      : beforeStr && beforeStr !== "—"
        ? t("change-text" as never, { field: label, old: beforeStr, new: afterStr })
        : t("change-to-text" as never, { field: label, new: afterStr });
    out.push({
      fieldLabel: label,
      oldValue: beforeStr,
      newValue: afterStr,
      type: "change",
      sentence,
    });
  }
  return out;
};

/** One field's before/after under a known label. Equal scalars: nothing. */
const diffValue = (
  label: string,
  before: unknown,
  after: unknown,
  fieldLabel: (key: string) => string,
  t: Translate,
  isCreate: boolean,
): FormattedFieldChange[] => {
  if (Array.isArray(before) || Array.isArray(after)) {
    return diffArrayField(before, after, label, fieldLabel, t, isCreate);
  }
  if (isRecord(before) && isRecord(after)) {
    return diffFields(before, after, label, fieldLabel, t, isCreate);
  }
  if (deepEqual(before, after)) return [];

  const beforeStr = passthrough(before);
  const afterStr = passthrough(after);
  const sentence = isCreate
    ? t("create-text" as never, { field: label, new: afterStr })
    : beforeStr && beforeStr !== "—"
      ? t("change-text" as never, { field: label, old: beforeStr, new: afterStr })
      : t("change-to-text" as never, { field: label, new: afterStr });
  return [
    {
      fieldLabel: label,
      oldValue: beforeStr,
      newValue: afterStr,
      type: "change",
      sentence,
    },
  ];
};

// --- single-log extraction (payload priority is driver policy) ---

/**
 * Legacy rows encode a JSON array of changed key names in
 * field_changed with empty field_changes, carrying the full snapshot
 * in old_value/new_value. Such rows must be diffed via the snapshot
 * path, not treated as a single field whose name is the array string.
 */
const isSnapshotRow = (log: AuditLogItem): boolean => {
  if (!log.field_changed) return true;
  try {
    return Array.isArray(JSON.parse(log.field_changed));
  } catch {
    return false;
  }
};

const extractLogChanges = (
  log: AuditLogItem,
  fieldLabel: (key: string) => string,
  t: Translate,
): FormattedFieldChange[] => {
  // A deleted entity gets one line, not a pile of per-field removals.
  if (normalizeActionType(log.action) === "DELETE") {
    return [
      {
        fieldLabel: t("data-deleted" as never),
        type: "delete",
        sentence: t("data-deleted" as never),
      },
    ];
  }
  const isCreate = normalizeActionType(log.action) === "CREATE";
  const skipOnCreate = (field: string): boolean =>
    isCreate && isIgnoredKey(field, IGNORED_KEYS);

  let raw: FormattedFieldChange[] = [];

  if (log.field_changes && log.field_changes.length > 0) {
    for (const change of log.field_changes as AuditFieldChange[]) {
      if (skipOnCreate(change.field)) continue;
      raw.push(
        ...diffValue(
          fieldLabel(change.field),
          safeParse(change.old_value),
          safeParse(change.new_value),
          fieldLabel,
          t,
          isCreate,
        ),
      );
    }
  } else if (log.field_changed && !isSnapshotRow(log)) {
    if (!skipOnCreate(log.field_changed)) {
      raw.push(
        ...diffValue(
          fieldLabel(log.field_changed),
          safeParse(log.old_value),
          safeParse(log.new_value),
          fieldLabel,
          t,
          isCreate,
        ),
      );
    }
  } else if (log.old_value || log.new_value) {
    const before = safeParse(log.old_value);
    const after = safeParse(log.new_value);
    if (isRecord(before) || isRecord(after)) {
      raw.push(...diffFields(before, after, undefined, fieldLabel, t, isCreate));
    } else {
      raw.push(...diffValue(t("details" as never), before, after, fieldLabel, t, isCreate));
    }
  }

  // Second net: drop changes whose rendered label still carries an
  // ignored key fragment (labels may carry prefixes).
  raw = raw.filter((change) => !containsIgnoredKey(change.fieldLabel, IGNORED_KEYS));

  // A create with no user-visible field changes still deserves a body.
  if (isCreate && raw.length === 0) {
    return [
      {
        fieldLabel: t("data-created" as never),
        type: "add",
        sentence: t("data-created" as never),
      },
    ];
  }
  return raw;
};

// --- entry assembly ---

/**
 * Build one display entry from a raw audit row. Exported for the
 * detail page's print-only audit appendix (see file header).
 */
const buildDriverHistoryEntry = (
  log: AuditLogItem,
  t: Translate,
): HistoryDataSidebarItem => ({
  id: log.id,
  timestampLabel: log.changed_at ? historyDateTimeFormatter(log.changed_at) : "—",
  rawTimestamp: log.changed_at,
  actorName: displayUserName(log.changed_by_name),
  actionType: normalizeActionType(log.action),
  actionLabel: normalizeActionLabel(log.action),
  fieldChanges: extractLogChanges(
    log,
    (key) => t(toSnakeCase(key) as never, { defaultValue: titleCase(key) }),
    t,
  ),
  reason: extractReason(log),
});

// --- hook ---

interface UseDriverHistoryOptions {
  driverId?: string;
  isOpen: boolean;
}

export const useDriverHistory = ({ driverId, isOpen }: UseDriverHistoryOptions) => {
  const { t: tHistory } = useAppTranslation("historyDataSidebar");
  const { i18n } = useTranslation();
  const [searchValue, setSearchValue] = useState("");

  const feed = useAuditLogFeed({
    entityType: "DRIVER",
    entityId: driverId ?? "",
    isOpen,
  });

  const items = useMemo(() => {
    const entries = feed.logs.map((log) => buildDriverHistoryEntry(log, tHistory));

    // Suppress update rows whose only changes are system bookkeeping —
    // they render as bare dots.
    return entries.filter(
      (item) =>
        item.actionType === "CREATE" || Boolean(item.reason) ||
        (item.fieldChanges?.length ?? 0) > 0,
    );
    // i18n.language: labels/sentences must re-resolve on language switch.
  }, [feed.logs, tHistory, i18n.language]);

  const filteredItems = useMemo(() => {
    if (!searchValue.trim()) return items;
    const query = searchValue.toLowerCase().trim();
    return items.filter((item) => {
      const matched =
        item.actorName.toLowerCase().includes(query) ||
        item.actionType?.toLowerCase().includes(query) ||
        item.actionLabel?.toLowerCase().includes(query) ||
        item.reason?.toLowerCase().includes(query) ||
        item.fieldChanges?.some(
          (change) =>
            change.fieldLabel.toLowerCase().includes(query) ||
            change.oldValue?.toLowerCase().includes(query) ||
            change.newValue?.toLowerCase().includes(query) ||
            change.sentence?.toLowerCase().includes(query),
        );
      return Boolean(matched);
    });
  }, [items, searchValue]);

  return {
    items: filteredItems,
    searchValue,
    setSearchValue,
    fromDateValue: feed.fromDateValue,
    toDateValue: feed.toDateValue,
    onDateChange: feed.setDateRange,
    isLoading: feed.state.isLoading,
    isFetching: feed.state.isFetching,
    isLoadingMore: feed.state.isLoadingMore,
    hasMore: feed.state.hasMore,
    total: feed.state.total,
    loadMore: feed.loadMore,
    state: feed.state,
  };
};

export { buildDriverHistoryEntry };
